import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGame } from '@/context/GameContext';
import { playClick, playSuccess } from '@/utils/sounds';
import {
  RetroBackground,
  RetroButton,
  bandedGradient,
  PALETTE,
  ACCENTS,
  DESIGN_TOKENS
} from '@/components/RetroUI';

export default function CoinToss() {
  const navigate = useNavigate();
  const { teamA, teamB, setBattingFirst, gameStarted } = useGame();
  
  const [flipping, setFlipping] = useState(false);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    if (!gameStarted) {
      navigate('/dashboard');
    }
  }, [gameStarted, navigate]);

  const handleFlip = () => {
    playClick();
    setFlipping(true);
    setShowResult(false);
    
    // Random result
    const battingFirst = Math.random() < 0.5 ? 'A' : 'B';
    
    setTimeout(() => {
      setFlipping(false);
      setResult(battingFirst);
      setBattingFirst(battingFirst);
      setShowResult(true);
      playSuccess();
    }, 2000);
  };

  const handleContinue = () => {
    playClick();
    navigate('/game');
  };

  const winningTeam = result === 'A' ? teamA.name : teamB.name;

  return (
    <RetroBackground variant="grass" className="flex flex-col items-center justify-center p-4">
      <div className="text-center max-w-lg w-full">
        <h1 style={{
          fontFamily: DESIGN_TOKENS.fonts.display,
          fontSize: DESIGN_TOKENS.fontSizes.displayLg,
          color: PALETTE.cream,
          textShadow: `3px 3px 0px ${PALETTE.ink}, 5px 5px 0px rgba(0,0,0,0.4)`,
          letterSpacing: '3px',
          marginBottom: '12px',
        }}>
          COIN TOSS
        </h1>
        <p style={{
          fontFamily: DESIGN_TOKENS.fonts.ui,
          fontWeight: 700,
          fontStyle: 'italic',
          fontSize: '18px',
          color: ACCENTS.gold,
          textShadow: `2px 2px 0px ${PALETTE.ink}`,
          marginBottom: '48px',
        }}>
          Who bats first?
        </p>
        
        {/* Coin */}
        <div className="flex justify-center mb-12">
          <div 
            className={`${flipping ? 'animate-coin-flip' : ''}`}
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: bandedGradient('#FFDE6B', '#F2C438', '#B8860B'),
              border: `4px solid ${PALETTE.ink}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '28px',
              color: PALETTE.ink,
              boxShadow: `
                inset 4px 4px 0px rgba(255,255,255,0.4),
                inset -4px -4px 0px rgba(0,0,0,0.3),
                6px 6px 0px ${PALETTE.ink}
              `,
            }}
            data-testid="coin"
          >
            {!flipping && showResult ? (result === 'A' ? 'A' : 'B') : '$'}
          </div>
        </div>
        
        {/* Teams Display */}
        <div className="flex justify-center items-center gap-6 mb-12">
          <div 
            className="relative"
            style={{
              padding: '16px 24px',
              background: PALETTE.cream,
              borderRadius: DESIGN_TOKENS.borderRadius.md,
              border: showResult && result === 'A' 
                ? `6px solid ${ACCENTS.gold}` 
                : `4px solid ${PALETTE.ink}`,
              boxShadow: showResult && result === 'A' 
                ? `0 0 20px ${ACCENTS.gold}, 4px 4px 0px ${PALETTE.ink}` 
                : `4px 4px 0px ${PALETTE.ink}`,
              transition: 'all 0.3s ease',
            }}
            data-testid="team-a-card"
          >
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontWeight: 700,
              fontSize: '15px',
              color: PALETTE.ink,
            }}>
              {teamA.name}
            </p>
          </div>
          
          <span style={{
            fontFamily: DESIGN_TOKENS.fonts.display,
            fontSize: '14px',
            color: PALETTE.cream,
            textShadow: `2px 2px 0px ${PALETTE.ink}`,
          }}>
            VS
          </span>
          
          <div 
            style={{
              padding: '16px 24px',
              background: PALETTE.cream,
              borderRadius: DESIGN_TOKENS.borderRadius.md,
              border: showResult && result === 'B' 
                ? `6px solid ${ACCENTS.gold}` 
                : `4px solid ${PALETTE.ink}`,
              boxShadow: showResult && result === 'B' 
                ? `0 0 20px ${ACCENTS.gold}, 4px 4px 0px ${PALETTE.ink}` 
                : `4px 4px 0px ${PALETTE.ink}`,
              transition: 'all 0.3s ease',
            }}
            data-testid="team-b-card"
          >
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.ui,
              fontWeight: 700,
              fontSize: '15px',
              color: PALETTE.ink,
            }}>
              {teamB.name}
            </p>
          </div>
        </div>
        
        {/* Result */}
        {showResult && (
          <div className="mb-8" data-testid="coin-toss-result">
            <p style={{
              fontFamily: DESIGN_TOKENS.fonts.display,
              fontSize: '14px',
              color: ACCENTS.gold,
              textShadow: `2px 2px 0px ${PALETTE.ink}, 0 0 10px ${ACCENTS.gold}66`,
              letterSpacing: '2px',
            }}>
              {winningTeam.toUpperCase()} BATS FIRST!
            </p>
          </div>
        )}
        
        {/* Buttons */}
        {!showResult ? (
          <RetroButton
            variant="danger"
            size="lg"
            onClick={handleFlip}
            disabled={flipping}
            data-testid="flip-coin-btn"
          >
            {flipping ? 'FLIPPING...' : 'FLIP COIN'}
          </RetroButton>
        ) : (
          <RetroButton
            variant="primary"
            size="lg"
            onClick={handleContinue}
            data-testid="start-game-btn"
          >
            PLAY BALL!
          </RetroButton>
        )}
      </div>
    </RetroBackground>
  );
}
